/**
 * Day-1 Retention Quick Win #2 — Streak Pill (永久 header chip).
 *
 * Spec: docs/research/day1-retention-k3-onboarding.md quick win #2.
 *
 * 與既有 streak-banner 區別:
 *   • streak-banner (src/learn/streak/streak-ui.ts) = 大型 promo banner,進入 banner
 *     priority queue,跟 dayone/badge/season/monster 競爭 2 個 slot,可能不出現。
 *   • StreakPill (本檔) = 永久 home header chip,每次 home render 都出現,目的是
 *     讓「我連續了 X 天」一直 visible → 損失趨避 + 累積感的視覺錨點。
 *
 * 學習與遊戲分離 (CLAUDE.md 紅線 + boss memory feedback_learn_pro_mode):
 *   pill 屬「home/academy navigation 層 retention banner」,可有彩光 + tooltip。
 *   絕對 NOT 注入 quiz/exam 作答畫面 (那邊由 pro-mode 強制 0 戲謔)。
 *
 * Reuse 既有 progress.streak.days schema (src/shared/types.ts:62) — 0 backend 改動。
 *
 * 學術風(boss 2026-06-08,見 wiki page-470f59c0):橘黃火苗漸層 → 學術綠/紙色系
 * (`--ef-*` token);🔥 glyph 保留當小點綴。色票一律取自 `theme.ts`,不自編色。
 */
import { ensureAcademicTheme } from '../ui/theme';

/** Display cap — 大於 365 顯示 "365+" 避免 overflow + 心理衝擊。 */
export const STREAK_DISPLAY_CAP = 365;

/** Gold milestone threshold — Day 7 解鎖限定塗裝。 */
export const STREAK_GOLD_THRESHOLD = 7;

/**
 * Mount / re-mount the streak pill into `container`.
 *
 * Idempotent: clears any prior pill (data-testid="streak-pill") in the
 * container before drawing the new one — safe to call on every home render.
 *
 * @param container header DOM element (e.g. h1 row in home.ts).
 * @param days current streak days from `progress.streak.days`. Undefined →
 *             treated as 0 (fresh player). Negative → throws (invalid state).
 * @throws RangeError when days is negative.
 */
export function renderStreakPill(container: HTMLElement, days: number | undefined): void {
  if (!container) return;

  // Normalise undefined → 0 (fresh player path)
  const raw = days === undefined ? 0 : days;
  if (typeof raw !== 'number' || Number.isNaN(raw)) {
    throw new RangeError(`renderStreakPill: days must be a number, got ${String(raw)}`);
  }
  if (raw < 0) {
    throw new RangeError(`renderStreakPill: days must be >= 0, got ${raw}`);
  }

  // Replace any prior pill (idempotent re-render safe)
  const prior = container.querySelector('[data-testid="streak-pill"]');
  if (prior) prior.remove();

  const isFresh = raw === 0;
  const capped = raw > STREAK_DISPLAY_CAP;
  const displayN = capped ? `${STREAK_DISPLAY_CAP}+` : String(raw);
  const isGold = !isFresh && raw >= STREAK_GOLD_THRESHOLD;

  const pill = document.createElement('div');
  pill.className = isFresh
    ? 'streak-pill streak-pill--fresh'
    : isGold
      ? 'streak-pill streak-pill--gold'
      : 'streak-pill streak-pill--active';
  pill.setAttribute('data-testid', 'streak-pill');
  pill.setAttribute('data-streak-days', String(raw));
  if (isGold) pill.setAttribute('data-streak-gold', 'true');
  if (isFresh) pill.setAttribute('data-streak-fresh', 'true');

  // 確保 `--ef-*` token 已注入(home.ts 不一定先呼;冪等)。學術風改套後 pill 的
  // 顏色全部走 token,但保留 inline-style 自含模式(不依賴全域 styles.css)。
  ensureAcademicTheme();

  // Inline base style — keeps pill self-contained without requiring a global
  // stylesheet update (matches dayone-banner / streak-banner inline pattern).
  const baseCss = [
    'display:inline-flex',
    'align-items:center',
    'gap:6px',
    'padding:6px 12px',
    'border-radius:var(--ef-radius-pill)',
    'font-family:var(--ef-sans)',
    'font-size:14px',
    'font-weight:700',
    'line-height:1',
    'cursor:default',
    'user-select:none',
    'transition:transform 120ms ease',
    'font-variant-numeric:tabular-nums',
  ].join(';');

  // 學術風配色(取自 theme.ts `--ef-*` token,不自編色):
  //   fresh = 紙底虛框灰墨(誠實的「還沒開始」placeholder)
  //   gold  = 學術綠實心(Day 7 里程碑,沉穩重點色而非金色彩光)
  //   active= 淺綠紙片描邊(進行中,克制)
  const variantCss = isFresh
    ? // 紙底虛框 placeholder（brand-new accounts)
      ';background:var(--ef-paper);color:var(--ef-sub);border:1px dashed var(--ef-line);'
    : isGold
      ? // Day 7+ 里程碑:學術綠實心
        ';background:var(--ef-green);color:var(--ef-paper-bright);' +
        'border:1px solid var(--ef-green-dark);box-shadow:var(--ef-shadow-card);'
      : // 進行中:淺學術綠紙片
        ';background:var(--ef-paper-bright);color:var(--ef-green-dark);' +
        'border:1px solid var(--ef-line);';

  pill.style.cssText = baseCss + variantCss;
  pill.setAttribute('title', tooltipFor(raw));
  pill.setAttribute('aria-label', tooltipFor(raw));

  if (isFresh) {
    pill.textContent = '開始今天的第 1 天 →';
  } else {
    const flame = document.createElement('span');
    flame.textContent = '🔥';
    flame.style.cssText = 'font-size:16px;line-height:1;';

    const count = document.createElement('span');
    count.textContent = displayN;
    count.style.cssText = 'font-size:14px;line-height:1;';

    pill.append(flame, count);
  }

  container.appendChild(pill);
}

/**
 * Tooltip copy.
 *   • days 0      → 招呼:鼓勵起步
 *   • days 1-6    → 「連續 X 天!再 (7-X) 天解鎖限定塗裝」
 *   • days 7      → 「連續 7 天!Day 7 限定塗裝已解鎖」(慶祝)
 *   • days 8-365  → 「連續 X 天!持續突破紀錄」
 *   • days > 365  → 「傳說大俠!連續 X 天 (顯示上限 365+)」
 *
 * Exported so tests can assert without DOM scraping.
 */
export function tooltipFor(days: number): string {
  if (days <= 0) return '今天還沒答題!按下開始建立你的連勝紀錄';
  if (days < STREAK_GOLD_THRESHOLD) {
    const remaining = STREAK_GOLD_THRESHOLD - days;
    return `連續 ${days} 天!再 ${remaining} 天解鎖 Day 7 限定塗裝`;
  }
  if (days === STREAK_GOLD_THRESHOLD) {
    return '連續 7 天!Day 7 限定塗裝已解鎖 🎉';
  }
  if (days > STREAK_DISPLAY_CAP) {
    return `傳說大俠!連續 ${days} 天 (顯示上限 ${STREAK_DISPLAY_CAP}+)`;
  }
  return `連續 ${days} 天!持續突破紀錄`;
}
